import { Box, Button, Dialog, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import React, { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfResourceLibraryItemFilter,
  IResourceLibraryDetail,
  IResourceLibraryItem,
  ListResponseOfResourceLibraryItem,
  ResourceLibraryItemFilter,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import ResourceLibraryItemCreateForm from "./ResourceLibraryItemCreateForm";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import persistanceStore from "stores/persistanceStore";
import { convertToUTC } from "utils/dateUtils";
import { isValid } from "date-fns";
import useDebounce from "hooks/useDebounce";

interface IResourceLibraryItemWithId extends IResourceLibraryItem {
  id: string;
}

type ResourceLibraryItemListProps = {
  resourceLibrary: IResourceLibraryDetail;
};

const useStyles = (theme: Theme) => ({
  rulesHeader: {
    display: "flex",
    justifyContent: "space-between",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
});

const ResourceLibraryItemsList = ({
  resourceLibrary,
}: ResourceLibraryItemListProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    resourceLibraryService,
    resourceLibraryItemDetailsUrl,
  } = useGlobalContext();

  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [createOpen, createOpenActions] = useBoolean(false);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const searchTerm = persistanceStore.useState(
    (s) => s.resourceLibraryItemFilterSearchTerm
  );
  const trimmedSearchTerm = searchTerm?.trim() ?? "";
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const dateFrom = persistanceStore.useState(
    (s) => s.resourceLibraryItemFilterDateFrom
  );
  const dateTo = persistanceStore.useState(
    (s) => s.resourceLibraryItemFilterDateTo
  );

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfResourceLibraryItem>(
    [
      "resourceLibraryItems",
      resourceLibrary.id,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
      dateFrom,
      dateTo,
    ],
    () =>
      resourceLibraryService.getResourceLibraryItems(
        resourceLibrary.id,
        ListRequestOfResourceLibraryItemFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: ResourceLibraryItemFilter.fromJS({
            searchText: debouncedSearchTerm,
            publicationDateFrom:
              dateFrom == null || !isValid(dateFrom)
                ? null
                : convertToUTC(dateFrom),
            publicationDateTo:
              dateTo == null || !isValid(dateTo) ? null : convertToUTC(dateTo),
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const resourceLibraryItems = data?.data as IResourceLibraryItemWithId[];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      type: "text",
      label: t({ id: "resourceLibraryItemListNameLabel" }),
    },
    {
      accessor: "resourceLibraryTypeName",
      customSortAccessor: "resourceLibraryType.name",
      type: "text",
      label: t({ id: "resourceLibraryItemListResourceTypeLabel" }),
    },
    {
      accessor: "publicationDate",
      type: "date",
      label: t({ id: "publicationDate" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(resourceLibraryItemDetailsUrl(resourceLibrary.id, id));
  };

  const onResourceLibraryCreated = () => {
    queryClient.invalidateQueries(["resourceLibraryItems"]);
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "resourceLibraryItemCreated" }))
    );
  };

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: resourceLibraryItems ?? [],
          loading: isFetching,

          dense: true,
          titleComponent: (
            <Box sx={styles.rulesHeader}>
              <Typography variant="h6">
                <T id="resourceLibraryItemListLabel" />
              </Typography>
              <Button
                onClick={createOpenActions.setTrue}
                startIcon={<AddIcon />}
                data-cy="addItemButton"
              >
                <T id="resourceLibraryFormAddItemButton" />
              </Button>
            </Box>
          ),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              resourceLibraryItemDetailsUrl(resourceLibrary.id, id)
            ),
          ],
        }}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <ResourceLibraryItemCreateForm
          resourceLibrary={resourceLibrary}
          onResourceLibraryItemCreated={onResourceLibraryCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default ResourceLibraryItemsList;
